import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from './Layout';
import { states } from '../states';
import { countries } from '../countries';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './StoreValue';
import { markStepComplete } from './StepGuard';
import { isPromotix } from '../utils/agentConfig';
import { FaEye, FaEyeSlash } from 'react-icons/fa';

const ContactForm = () => {
  const initialContactState = {
    guid: '',
    contactOrder: '1',
    role: 'Owner',
    ownershipPercent: '',
    socialSecurityNumber: '',
    driversLicenseNumber: '',
    driversLicenseRegion: '',
    driversLicenseExpiration: '',
    phone: '',
    mobilePhone: '',
    firstName: '',
    lastName: '',
    email: '',
    address: '',
    city: '',
    region: '',
    postal: '',
    country: 'USA',
    dateOfBirth: ''
  };
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const [loading, setLoading] = useState(false);
  const [contacts, setContacts] = useState([initialContactState]);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [ownershipType, setOwnershipType] = useState('');
  const isNonProfit = ownershipType === 'NonProfit';
  const navigate = useNavigate();
  const { ContactGuid,agentIdVal,leadGuid, loginInfo, updateAgentId, updateContactGuid, updateLeadGuid } = useAuth();
const [fieldErrors, setFieldErrors] = useState({});
  // Per-contact reveal toggle for masked emails (keyed by contact index).
  const [revealedEmails, setRevealedEmails] = useState({});
  // The only unmasked email we hold client-side is this session's login email, which is the Owner
  // contact's. TRX returns stored emails masked and has no unmask API, so the eye can reveal the
  // full address for the Owner contact only, and only while we still have it (lost on hard refresh).
  const loginEmail = (loginInfo?.email && !String(loginInfo.email).includes('*')) ? loginInfo.email : '';
  const handleChange = (index, e) => {
    const { name, value } = e.target;
    // Promotix: ZIP/postal is restricted to 5 digits — strip non-digits and cap length as typed.
    const v = (name === 'postal' && isPromotix(agentIdVal))
      ? value.replace(/\D/g, '').slice(0, 5)
      : value;
    const newContacts = [...contacts];
    newContacts[index][name] = v;
    setContacts(newContacts);
    setFieldErrors(prevErrors => ({
      ...prevErrors,
      [`${index}-${name}`]: ''
    }));
  };

  const addContactForm = () => {
    if (contacts.length < 4) {
      const newContact = { ...initialContactState };
      if (isNonProfit) {
        newContact.role = 'ControlProng';
      }
      setContacts([...contacts, newContact]);
    }
  };
  const today = new Date();
  const todayString = `${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}-${today.getFullYear()}`;
  
  const removeContactForm = async (index) => {
  setLoading(true); // Start loading
  setErrorMessage(''); // Clear previous error messages
  setSuccessMessage(''); // Clear previous success messages

  try {
    const contactToRemove = contacts[index];

    if (contactToRemove && contactToRemove.guid) {
      // Prepare the API request for deletion
      const data = {
        leadguidVal: leadGuid,
        agentId: agentIdVal,
        ContactGuidVal: contactToRemove.guid, // Make sure this key is correct for deletion
        Detail: {
          TranType: "Remove",    // Correct TranType for deletion
          TranAction: "RemoveContact", // Correct TranAction for deletion
          Ip: "Ip",
        },
        Contact: {
          guid: contactToRemove.guid,
        },
      };

      const response = await axios.post(`${API_BASE_URL}/api/Auth/LeadModify`, data);

      const responseCode = response?.data?.response?.result?.responseCode;
      const errorInfo = response.data.response.result?.debugInfo;

      if (responseCode === '00' || responseCode === 'X7') {
        const newContacts = contacts.filter((_, i) => i !== index);
        setContacts(newContacts);

        // Also, clear any field-specific errors associated with the removed contact
        setFieldErrors(prevErrors => {
            const updatedErrors = { ...prevErrors };
            Object.keys(updatedErrors).forEach(key => {
                if (key.startsWith(`${index}-`)) {
                    delete updatedErrors[key];
                }
            });
            return updatedErrors;
        });
        setSuccessMessage('Contact removed successfully!');
        // Clear success message after a short delay
        setTimeout(() => setSuccessMessage(''), 3000);

      } else {
        setErrorMessage(errorInfo);
      }
    } else {
      // Case: Contact does NOT have a GUID (it's a newly added, unsaved contact)
      // Just remove it from the local state if there's more than one contact.
      if (contacts.length > 1) {
        const newContacts = contacts.filter((_, i) => i !== index);
        setContacts(newContacts);
        // Clear errors for the client-side removed contact
        setFieldErrors(prevErrors => {
            const updatedErrors = { ...prevErrors };
            Object.keys(updatedErrors).forEach(key => {
                if (key.startsWith(`${index}-`)) {
                    delete updatedErrors[key];
                }
            });
            return updatedErrors;
        });
        setSuccessMessage('Unsaved contact removed from list.');
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
          // If it's the last unsaved contact, reset to initial state
          setContacts([{ ...initialContactState }]);
          setFieldErrors({}); // Clear all errors if resetting to initial state
          setSuccessMessage('Contact list reset.');
          setTimeout(() => setSuccessMessage(''), 3000);
      }
    }
  } catch (error) {
    console.error("Error removing contact:", error);
    setErrorMessage("An error occurred while removing the contact. Please try again.");
  } finally {
    setLoading(false); // Stop loading after response
  }
};
  

  useEffect(() => {
    const fetchLeadData = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/Auth/LeadLookup`, {
                params: {
                    leadGuidVal: leadGuid,
                    agentIdValue: agentIdVal,
                    // Pass leadGuid here
                },
                });
        const leadData = response.data;
        if (leadData.result.responseCode === '00') {
          const fetchedOwnership = leadData.queryResponse?.business?.ownershipType || '';
          setOwnershipType(fetchedOwnership);
          const leadIsNonProfit = fetchedOwnership === 'NonProfit';

          const contactsData = leadData.queryResponse.contacts.contact;
          setContacts(contactsData.slice(0, 4).map((contact, i) => {
  const originalSSN = contact.socialSecurityNumber || '';
  let maskedSSN = originalSSN;

  if (originalSSN && originalSSN.length === 11) {
    maskedSSN = `***-**-${originalSSN.slice(-4)}`;
  }

  let normalizedRole = contact.role === 'Control prog' ? 'ControlProng' : (contact.role || '');
  if (leadIsNonProfit && normalizedRole === 'Owner') {
    normalizedRole = 'ControlProng';
  }

  return {
    guid: contact.guid || '',
    contactOrder: contact.contactOrder || `${i + 1}`,
    role: normalizedRole,
    ownershipPercent: contact.ownershipPercent || '',
    // Keep both original + masked
    socialSecurityNumber: maskedSSN,
    originalSocialSecurityNumber: originalSSN,
    driversLicenseNumber: contact.driversLicenseNumber || '',
    // Track the value loaded from the server so re-save can tell "untouched" from
    // "edited" and avoid overwriting the stored DL with a masked/truncated value.
    originalDriversLicenseNumber: contact.driversLicenseNumber || '',
    driversLicenseRegion: contact.driversLicenseRegion || '',
    driversLicenseExpiration: formatDateToYYYYMMDD(contact.driversLicenseExpiration || ''),
    dateOfBirth: formatDateToYYYYMMDD(contact.dateOfBirth || ''),
    phone: contact.phone || '',
    mobilePhone: contact.cellPhone || '',
    firstName: contact.firstName || '',
    lastName: contact.lastName || '',
    email: contact.email || '',
    address: contact.address || '',
    city: contact.city || '',
    region: contact.region || '',
    postal: contact.postal || '',
    country: contact.country || 'USA'
  };
}));

        }
      } catch (error) {
        console.error('Error fetching lead data:', error);
      }
    };

    fetchLeadData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setSuccessMessage('');
    if (!leadGuid) {
        setErrorMessage('Session expired or lead data is missing. Please start over from the login page.');
        return;
    }
    setLoading(true);

    const missingFields = [];
  let totalOwnershipPercent = 0;

  const today = new Date();
  const todayISO = today.toISOString().split('T')[0]; // Format today's date as YYYY-MM-DD

  // Validate fields and calculate total ownership for Owners
  for (const contact of contacts) {
    if (!contact.contactOrder) missingFields.push('Contact Order');
    if (!contact.role) missingFields.push('Role');
    if (["Owner", "ControlProng"].includes(contact.role)) {
      if (contact.role === "Owner") {
        if (!contact.ownershipPercent) missingFields.push('Ownership Percent');
        totalOwnershipPercent += parseFloat(contact.ownershipPercent || 0);
      }
      if (!contact.socialSecurityNumber) missingFields.push('Social Security Number');
      if (!contact.driversLicenseNumber) missingFields.push('Drivers License Number');
      if (!contact.driversLicenseRegion) missingFields.push('Drivers License Region');
    }
    if (!contact.lastName) missingFields.push('Last Name');
    if (!contact.email) missingFields.push('Email');
    if (!contact.firstName) missingFields.push('First Name');
    if (!contact.address) missingFields.push('Address');
    if (!contact.city) missingFields.push('City');
    if (!contact.region) missingFields.push('State');
    if (!contact.postal) missingFields.push('Postal');
    if (isPromotix(agentIdVal) && contact.postal && !/^\d{5}$/.test(contact.postal)) {
      setErrorMessage('ZIP code must be exactly 5 digits');
      setLoading(false);
      return;
    }
    if (!contact.phone) missingFields.push('Phone');
    if (!contact.country) missingFields.push('Country');

    // Birthday validation
    if (!contact.dateOfBirth) {
      missingFields.push('Date Of Birth');
    } else if (new Date(contact.dateOfBirth) > today) {
      setErrorMessage('Date of Birth cannot be in the future.');
      setLoading(false); 
      return;
    }

    // Driver's license expiration validation
    if (!contact.driversLicenseExpiration) {
      missingFields.push('Driver\'s License Expiration');
    } else if (new Date(contact.driversLicenseExpiration) < today) {
      setErrorMessage('Driver\'s License Expiration cannot be in the past.');
      setLoading(false); 
      return;
    }
  }

  if (missingFields.length > 0) {
    setErrorMessage(`Please fill in the following required fields: ${missingFields.join(', ')}`);
    setLoading(false); 
    return;
  }

  // Check for Phone number errors
  if (phoneError) {
    setErrorMessage(phoneError);
    setLoading(false);
    return; // Stop form submission
  }

  if (phoneError1) {
    setErrorMessage(phoneError1);
    setLoading(false);
    return; // Stop form submission
  }

  if (SSNError) {
    setErrorMessage(SSNError);
    setLoading(false);
    return; // Stop form submission
  }

  // Ownership percent check (OUTSIDE the loop)
  const ownerContacts = contacts.filter(c => c.role === "Owner");
  if (ownerContacts.length > 0) {
    if (totalOwnershipPercent < 100) {
      const remainingOwnership = (100 - totalOwnershipPercent);
      const addOwner = window.confirm(
        `Total ownership is ${totalOwnershipPercent}%. Would you like to add a new owner with the remaining ${remainingOwnership}%? Click OK to add an owner, or Cancel to continue to the next page.`
      );
      if (addOwner) {
        setContacts([
          ...contacts,
          {
            ...initialContactState,
            role: 'Owner',
            ownershipPercent: remainingOwnership,
          },
        ]);
        setLoading(false);
        return; // Stay on page so user can fill new owner or submit again
      }
      // User chose Cancel: proceed with submission (allow going to next page)
    } else if (totalOwnershipPercent > 100) {
      alert("Total ownership percentage cannot exceed 100%. Please adjust the values.");
      setLoading(false);
      return; // Prevent submission
    }
  }
  
    try {
      const contactRequests = contacts.map(contact => {
        const baseContactData = {
          leadguidVal: leadGuid,
          agentId: agentIdVal,
          Detail: {
            TranType: "TranType",
            TranAction: "TranAction",
            Ip: "Ip",
          },
          Contact: {
            contactOrder: contact.contactOrder,
            role: contact.role,
            phone: formatPhoneNumber(contact.phone),
            // Mobile phone is optional. Only send it when entered; an empty string fails TRX's
            // PhoneCellType pattern, whereas undefined is dropped from the payload (field omitted).
            cellPhone: contact.mobilePhone ? formatPhoneNumber(contact.mobilePhone) : undefined,
            firstName: contact.firstName,
            lastName: contact.lastName,
            address: contact.address,
            city: contact.city,
            region: contact.region,
            postal: contact.postal,
            country: contact.country,
            driversLicenseExpiration: formatDateToMMDDYYYY(contact.driversLicenseExpiration),
            dateOfBirth: formatDateToMMDDYYYY(contact.dateOfBirth)
          },
        };

        // Only re-send SSN/DL when the user actually entered/changed them this session.
        // After a Login/resume, LeadLookup returns the SSN already masked ("***-**-6768");
        // re-sending that mask makes TRX store "000-00-6768". Omitting the field instead
        // leaves TRX's stored value untouched (backend serializes with WhenWritingNull),
        // mirroring how the email field is handled below.
        const ssnEntered = contact.socialSecurityNumber &&
          !contact.socialSecurityNumber.includes("*");
        // DL has no mask marker, so compare against the value loaded from the server.
        const dlEntered = contact.driversLicenseNumber &&
          contact.driversLicenseNumber !== (contact.originalDriversLicenseNumber || '');

        if (["Owner"].includes(contact.role)) {
          baseContactData.Contact.ownershipPercent= contact.ownershipPercent;
        }
        if (["Owner","ControlProng"].includes(contact.role)) {
          if (ssnEntered) {
            baseContactData.Contact.socialSecurityNumber = contact.socialSecurityNumber;
          }
          if (dlEntered) {
            baseContactData.Contact.driversLicenseNumber = contact.driversLicenseNumber;
          }
          baseContactData.Contact.driversLicenseRegion= contact.driversLicenseRegion;
        }
    
        // Add the email only if it's valid (not masked)
        if (contact.email && !contact.email.includes("*")) {
          baseContactData.Contact.email = contact.email;
        }
    
        // If the contact has a guid, include it in the request
        if (contact.guid !== "") {
          baseContactData.Contact.guid = contact.guid;
        }
    
        return baseContactData;
      });
    
      const responses = [];
      for (const payload of contactRequests) {
        const response = await axios.post(`${API_BASE_URL}/api/Auth/LeadModify`, payload);
        responses.push(response);
        const code = response.data.response.result.responseCode;
        if (code !== '00' && code !== 'X7') {
          break;
        }
      }

      const failedResponses = responses.filter(
        r => r.data.response.result.responseCode !== '00' && r.data.response.result.responseCode !== 'X7'
      );

      if (failedResponses.length === 0) {
        markStepComplete('contactInfo');
        navigate('/BusinessInfo');
      } else {
        const errorMessages = failedResponses
          .map(r => r.data.response.result?.debugInfo || r.data.response.result?.errorInfo || 'Unknown error')
          .join('; ');
        setErrorMessage(errorMessages);
      }

    } catch (error) {
      const serverMsg = error.response?.data?.message || error.response?.data || error.message;
      setErrorMessage(typeof serverMsg === 'string' ? serverMsg : 'Error saving contacts.');
    } finally {
      setLoading(false);
    }
  };
  
  

  const formatDateToMMDDYYYY = (date) => {
    const [year, month, day] = date.split("-");
    return `${month}/${day}/${year}`;
  };

  const formatDateToYYYYMMDD = (date) => {
    const [month, day, year] = date.split('/');
    return `${year}-${month}-${day}`;
  };

  
  const [phoneError, setPhoneError] = useState("");
  const [phoneError1, setPhoneError1] = useState("");
  const [SSNError, setSSNError] = useState("");

  const handleSSNBlur = (index, e) => {
    const rawSSN = e.target.value.replace(/\D/g, ''); // Remove non-numeric characters
    let formattedSSN = rawSSN;
  
    if (rawSSN.length === 9) {
      formattedSSN = rawSSN.replace(/(\d{3})(\d{2})(\d{4})/, '$1-$2-$3');
      setSSNError('');  // Clear error if the SSN number is valid
    } else {
      setSSNError('SSN number must contain exactly 9 digits with format with 711-11-1111');
    }
    const updatedContacts = [...contacts];
    updatedContacts[index].socialSecurityNumber = formattedSSN;
    setContacts(updatedContacts);
  
  };

// Normalize to the backend's dashed format (xxx-xxx-xxxx). Idempotent; returns
// the value unchanged when it isn't 10 digits.
const formatPhoneNumber = (phone) => {
  const digits = (phone || '').replace(/\D/g, '');
  return digits.length === 10 ? digits.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3') : phone;
};

const handlePhoneBlur = (index, e) => {
  const rawPhone = e.target.value.replace(/\D/g, ''); // Remove non-numeric characters
  let formattedPhone = rawPhone;

  if (rawPhone.length === 10) {
    formattedPhone = rawPhone.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3');
    setPhoneError('');  // Clear error if the phone number is valid
  } else {
    setPhoneError('Phone number must contain exactly 10 digits with format with 711-111-1111');
  }


  const updatedContacts = [...contacts];
  updatedContacts[index].phone = formattedPhone;
  setContacts(updatedContacts);

};

const handlePhoneBlur1 = (index, e) => {
  const rawPhone = e.target.value.replace(/\D/g, ''); // Remove non-numeric characters
  let formattedPhone = rawPhone;

  if (!rawPhone) {
    setPhoneError1('');
    return;
  }

  if (rawPhone.length === 10) {
    formattedPhone = rawPhone.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3');
    setPhoneError1('');
  } else {
    setPhoneError1('Phone number must be 10 digits long.');
  }

  const updatedContact = [...contacts];
  updatedContact[index].mobilePhone = formattedPhone;
  setContacts(updatedContact);

};

  return (
    <div>
      {loading && (
                <div className="loading-overlay">
                <div className="spinner-border" role="status">
                </div>
                </div>
        )}
    <Layout pageTitle="Contact Information">
        <form onSubmit={handleSubmit}>
          <div className="alert alert-info mb-4" role="alert">
            <strong>Disclaimer:</strong> 25% or more ownership must have an owner contact.
          </div>
          {contacts.map((contact, index) => (
            <div className="contact-section" key={index}>
               <div className="batch-header d-flex align-items-center justify-content-between">
               <h3>Contact {index + 1} {index < 1 && (
    <button
      type="button"
      className="btn btn-info"
      onClick={addContactForm}
      disabled={contacts.length >= 4}
    >
      Add Contact
    </button>
  )}</h3> 
  {contacts.length > 1 && (
    <button
      type="button"
      className="btn btn-danger mt-2 ms-3"
      onClick={() => removeContactForm(index)}
    >
      Remove
    </button>)}</div>
              
              <div className="row">
              <div className="col-md-4">
                <label className='label_Control'>Contact Type <span style={{ color: 'red', display: 'contents' }}>*</span></label>
                <select className="form-controling" name="contactOrder" value={contact.contactOrder} onChange={(e) => handleChange(index, e)}>
                  <option value="1">Primary</option>
                  <option value="2">Secondary</option>
                  <option value="3">Supplementary</option>
                </select>
              </div>
              <div className="col-md-4">
                <label className='label_Control'>Role <span style={{ color: 'red', display: 'contents' }}>*</span></label>
                <select className="form-controling" name="role" value={contact.role} onChange={(e) => handleChange(index, e)}>
                <option value="">Please Select</option>
                  {!isNonProfit && <option value="Owner">Owner</option>}
                  <option value="Administrator">Administrator</option>
                  <option value="ControlProng">ControlProng</option>
                  <option value="Business">Business</option>
                  <option value="Technical">Technical</option>
                  <option value="Support">Support</option>
                </select>
              </div>
              {["Owner"].includes(contact.role) && (
  <>
              <div className="col-md-4">
                <label className='label_Control'>Ownership Percentage <span style={{ color: 'red', display: 'contents' }}>*</span></label>
                <input type="text" className="form-controling" name="ownershipPercent" value={contact.ownershipPercent} onChange={(e) => handleChange(index, e)} required />
              </div>
              </>
)}
            </div>
            {/* Conditionally render the other fields */}
            {["Owner", "ControlProng"].includes(contact.role) && (
              <>
            <div className="row mt-3">
              <div className="col-md-4">
                <label className='label_Control'>Social Security Number <span style={{ color: 'red', display: 'contents' }}>*</span></label>
                <input type="text" className="form-controling" name="socialSecurityNumber" value={contact.socialSecurityNumber} onChange={(e) => handleChange(index, e)} onBlur={(e) => handleSSNBlur(index, e)} placeholder="111-11-1111" required />
                {SSNError && <p className="text-danger">{SSNError}</p>}
              </div>
              <div className="col-md-4">
                <label className='label_Control'>Driver License Number <span style={{ color: 'red', display: 'contents' }}>*</span></label>
                <input type="text" className="form-controling" name="driversLicenseNumber" value={contact.driversLicenseNumber} onChange={(e) => handleChange(index, e)} required />
              </div>
              <div className="col-md-4">
                <label className='label_Control'>Driver License State <span style={{ color: 'red', display: 'contents' }}>*</span></label>
                <select className="form-controling" name="driversLicenseRegion" value={contact.driversLicenseRegion} onChange={(e) => handleChange(index, e)}>
                  {states.map((state, i) => (
                    <option key={i} value={state.abbreviation}>{state.name}</option>
                  ))}
                </select>
              </div>
            </div>
</>
)}
            <div className="row mt-3">
              <div className="col-md-4">
                <label className='label_Control'>First Name <span style={{ color: 'red', display: 'contents' }}>*</span></label>
                <input type="text" className="form-controling" name="firstName" value={contact.firstName} onChange={(e) => handleChange(index, e)} required />
              </div>
              <div className="col-md-4">
                <label className='label_Control'>Last Name <span style={{ color: 'red', display: 'contents' }}>*</span></label>
                <input type="text" className="form-controling" name="lastName" value={contact.lastName} onChange={(e) => handleChange(index, e)} required />
              </div>
              <div className="col-md-4">
                <label className='label_Control'>Email <span style={{ color: 'red', display: 'contents' }}>*</span></label>
                {(() => {
                  const masked = !!contact.email && String(contact.email).includes('*');
                  const revealable = contact.role === 'Owner' ? loginEmail : '';
                  const shown = !!revealedEmails[index];
                  return (
                    <div style={{ position: 'relative' }}>
                      <input
                        type={masked && shown ? 'text' : 'email'}
                        className="form-controling"
                        name="email"
                        value={masked && shown && revealable ? revealable : contact.email}
                        onChange={(e) => handleChange(index, e)}
                        onFocus={() => setRevealedEmails((p) => ({ ...p, [index]: false }))}
                        required
                        style={masked ? { paddingRight: '2.4rem' } : undefined}
                      />
                      {masked && (
                        <button
                          type="button"
                          onClick={() => setRevealedEmails((p) => ({ ...p, [index]: !p[index] }))}
                          disabled={!revealable}
                          title={revealable ? (shown ? 'Hide email' : 'Show email') : 'Sign in again to view the full email'}
                          aria-label={shown ? 'Hide email' : 'Show email'}
                          style={{
                            position: 'absolute', right: '0.6rem', top: '50%', transform: 'translateY(-50%)',
                            background: 'none', border: 'none', padding: 0, lineHeight: 0,
                            color: revealable ? '#0d6efd' : '#adb5bd',
                            cursor: revealable ? 'pointer' : 'not-allowed',
                          }}
                        >
                          {shown ? <FaEyeSlash /> : <FaEye />}
                        </button>
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>

            <div className="row mt-3">
              <div className="col-md-4">
                <label className='label_Control'>Phone <span style={{ color: 'red', display: 'contents' }}>*</span></label>
                <input type="text" className="form-controling" name="phone" value={contact.phone} onChange={(e) => handleChange(index, e)} onBlur={(e) => handlePhoneBlur(index, e)} placeholder="111-111-1111" required />
                {phoneError && <p className="text-danger">{phoneError}</p>}
              </div>
              <div className="col-md-4">
                <label className='label_Control'>Mobile Phone</label>
                <input type="text" className="form-controling" name="mobilePhone" value={contact.mobilePhone} onChange={(e) => handleChange(index, e)} onBlur={(e) => handlePhoneBlur1(index, e)} placeholder="111-111-1111" />
                {phoneError1 && <p className="text-danger">{phoneError1}</p>}
              </div>
              <div className="col-md-4">
                <label className='label_Control'>Address <span style={{ color: 'red', display: 'contents' }}>*</span></label>
                <input type="text" className="form-controling" name="address" value={contact.address} onChange={(e) => handleChange(index, e)} required />
              </div>
            </div>

            <div className="row mt-3">
              <div className="col-md-4">
                <label className='label_Control'>City <span style={{ color: 'red', display: 'contents' }}>*</span></label>
                <input type="text" className="form-controling" name="city" value={contact.city} onChange={(e) => handleChange(index, e)} required />
              </div>
              <div className="col-md-4">
                <label className='label_Control'>State <span style={{ color: 'red', display: 'contents' }}>*</span></label>
                <select className="form-controling" name="region" value={contact.region} onChange={(e) => handleChange(index, e)}>
                  {states.map((state, i) => (
                    <option key={i} value={state.abbreviation}>{state.name}</option>
                  ))}
                </select>
              </div>
              <div className="col-md-4">
                <label className='label_Control'>Postal Code <span style={{ color: 'red', display: 'contents' }}>*</span></label>
                <input type="text" className="form-controling" name="postal" value={contact.postal} onChange={(e) => handleChange(index, e)} {...(isPromotix(agentIdVal) ? { maxLength: 5, inputMode: 'numeric' } : {})} required />
              </div>
            </div>
        
            <div className="row mt-3">
  <div className="col-md-4">
    <label className='label_Control'>
      Country <span style={{ color: 'red', display: 'contents' }}>*</span>
    </label>
    <select 
      id="country" 
      name="country" 
      className="form-controling" 
      value={contact.country} 
      onChange={(e) => handleChange(index, e)}
      required
    >
      <option value="">Select a country</option>
      {countries.map((country) => (
        <option key={country.abbreviation} value={country.abbreviation}>
          {country.name}
        </option>
      ))}
    </select>
  </div>

  <div className="col-md-4">
    <label className='label_Control'>
      Date of Birth <span style={{ color: 'red', display: 'contents' }}>*</span>
    </label>
    <input
      type="date"
      name="dateOfBirth"
      className="form-controling"
      value={contact.dateOfBirth}
      onChange={(e) => handleChange(index, e)}
       min="1800-01-01"
       max="2099-12-31"
      required
    />
  </div>

  <div className="col-md-4">
    <label className='label_Control'>
      Driver's License Expiration <span style={{ color: 'red', display: 'contents' }}>*</span>
    </label>
    <input
      type="date"
      name="driversLicenseExpiration"
      className="form-controling"
      value={contact.driversLicenseExpiration}
      onChange={(e) => handleChange(index, e)}
       min="1800-01-01"
       max="2099-12-31"
      required
    />
  </div>
</div>
              
            </div>
          ))}
          
          <div className="d-flex justify-content-center mt-3">
    <button type="submit" className="btn btn-success px-5" style={{ width: '370px' }}>
        Next
    </button>
</div>
 {/* Show loading image when waiting for response */}
          {errorMessage && <p className="error-message">{errorMessage}</p>}
          {successMessage && <p className="success-message">{successMessage}</p>}
          <p style={{ color: 'red' }}>* Required field</p>
        </form>
    </Layout>
    </div>
  );
};

export default ContactForm;